<template>
	<div
		:class="{ 'document-preview-container': true, 'is-loading': !isLoaded }"
		:style="mediaCssVars"
		ref="previewContainer"
	>
		<figure
			v-show="isLoaded && !loadingError"
			:class="{ clickable: expandOnClick }"
			@click="openDocumentPreviewModal(document)"
		>
			<video
				v-if="mediaType === ValidFileType.Video"
				controls
				@loadeddata="onDocLoad(true)"
				@error="onDocLoad(false)"
			>
				<source
					:src="downloadLink"
					:type="document.docInfo.fileType"
				/>
				<p>Unable to display video.</p>
			</video>

			<img
				v-else-if="mediaType === ValidFileType.Image"
				:src="downloadLink"
				:alt="document.docInfo.fileName"
				@load="onDocLoad(true)"
				@error="onDocLoad(false)"
			/>

			<div
				v-else-if="mediaType === ValidFileType.PDF"
				class="pdf-container"
			>
				<PDFViewer
					:customDocumentInitParams="customPDFInitParams"
					:pageLimit="pdfPagesToRender"
					:pageWidth="pdfWidth"
					@pdfLoaded="onPdfLoaded"
				/>
			</div>

			<div
				v-else
				class="not-supported-container"
			>
				<p class="warning-message"><i class="fas fa-info-circle" /> File type not supported.</p>

				<p><b>File Name:</b> {{ document.docInfo.fileName }}</p>
				<p><b>File Type:</b> {{ document.docInfo.fileType }}</p>
				<p><b>Date Uploaded:</b> {{ timestampFormatted }}</p>
			</div>

			<figcaption v-show="!hideCaption">{{ docTypeDisplay }}</figcaption>
		</figure>

		<LoaderComponent v-show="!isLoaded && !loadingError" />

		<div v-if="loadingError">
			<p class="warning-message"><i class="fas fa-info-circle" /> Error loading file.</p>
		</div>
	</div>
</template>
<script lang="ts">
	export interface IDocumentPreviewProps {
		document: IUploadedDocument;

		/** A CSS width value */
		mediaWidth?: string;

		/** A CSS height value */
		mediaHeight?: string;

		/** A brief description of the media that will appear on top of the document. If not provided, will default to the document type. */
		caption?: string;

		/** When true, clicking on the preview of the document will open the Document Preview Modal */
		expandOnClick?: boolean;

		/** This is only applicable when `expandOnClick` is true. Will hide any fields-to-match on the document when that modal gets opened. */
		hideFieldsToMatch?: boolean;

		/** When true, no caption will appear on top of the document. */
		hideCaption?: boolean;
	}
</script>
<script setup lang="ts">
	import { computed, onMounted, ref, useTemplateRef } from 'vue';

	import $modal from '@core/services/modal';
	import auth from '@core/services/auth';
	import settings from 'settings';
	import util from '@core/services/util';

	import { DocumentInitParameters, PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
	import IDocumentType from '@core/_interfaces/DocumentType';
	import { IUploadedDocument } from '@core/classes/UploadedDocument';

	import modalDocumentPreview from '@core/modals/modalDocumentPreview.vue';

	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import PDFViewer from './PDFViewer.vue';

	enum ValidFileType {
		Video = 'video',
		Image = 'image',
		PDF = 'pdf',
		NotSupported = 'notSupported',
	}

	const props = withDefaults(defineProps<IDocumentPreviewProps>(), { mediaWidth: '100%', mediaHeight: '100%' });
	const emit = defineEmits<{ load: []; error: [] }>();

	const previewContainerRef = useTemplateRef<HTMLDivElement>('previewContainer');

	const isLoaded = ref(false);
	const loadingError = ref(false);

	const downloadLink = computed((): string => {
		return `${settings.apiUrl}/Documents/Download/${props.document.id}`;
	});

	const mediaType = computed((): ValidFileType => {
		const fileType = props.document.docInfo.fileType.toLowerCase();
		if (fileType.indexOf('video') !== -1) return ValidFileType.Video;
		else if (fileType.indexOf('image') !== -1) return ValidFileType.Image;
		else if (fileType.indexOf('pdf') !== -1) return ValidFileType.PDF;
		else return ValidFileType.NotSupported;
	});

	const mediaCssVars = computed((): string => {
		return `--media-width:${props.mediaWidth}; --media-height:${props.mediaHeight};`;
	});

	const timestampFormatted = computed((): string => {
		return util.formatDate(props.document.docInfo.timestamp, 'long', 'medium');
	});

	const docTypeDisplay = computed((): string => {
		let pageCount = '';
		if (mediaType.value === ValidFileType.PDF && pdfPagesToRender.value != undefined) pageCount = `(1/${totalPDFPages.value})`;

		let captionText = '';
		const foundType = settings.lookups?.uploadedDocumentTypes?.find((d: IDocumentType) => d.code === props.document.docInfo.docType);

		if (props.caption) captionText = props.caption;
		else if (!foundType) captionText = props.document.docInfo.fileName;
		else captionText = foundType.display;

		return `${captionText} ${pageCount}`.trim();
	});

	// #region PDF Data
	const totalPDFPages = ref(0);

	/** When `expandOnClick` is enabled, only render the 1st page of the PDF. Otherwise, returns `undefined` to indicate that all PDF pages should be rendered. */
	const pdfPagesToRender = computed((): number => {
		return props.expandOnClick ? 1 : undefined;
	});

	const customPDFInitParams = computed((): DocumentInitParameters => {
		return {
			url: downloadLink.value,
			httpHeaders: {
				'Cache-Control': 'no-cache',
				'Content-Type': 'application/json',
				'Access-Control-Allow-Origin': '*',
				Authorization: `Bearer ${auth.getToken()}`,
			},
			verbosity: 0,
		};
	});

	const pdfWidth = computed((): number | string => {
		if (props.mediaWidth.indexOf('px') !== -1) {
			const pxIndex = props.mediaWidth.indexOf('px');
			return props.mediaWidth.substring(0, pxIndex);
		}

		// Defaults to fill container
		return previewContainerRef.value ? previewContainerRef.value.offsetWidth : 1000;
	});

	function onPdfLoaded(pdf: PDFDocumentProxy) {
		totalPDFPages.value = pdf.numPages;
		onDocLoad(true);
	}
	// #endregion

	// #region Lifecycle Hooks
	onMounted(async () => {
		if (mediaType.value === ValidFileType.NotSupported) isLoaded.value = true;
	});
	// #endregion

	function onDocLoad(loadSuccess: boolean) {
		isLoaded.value = true;
		loadingError.value = !loadSuccess;

		if (loadingError.value) emit('error');
		else emit('load');
	}

	function openDocumentPreviewModal(document: IUploadedDocument) {
		if (!props.expandOnClick) return;

		let modalTitle = docTypeDisplay.value;
		if (mediaType.value === ValidFileType.PDF) modalTitle = modalTitle.substring(0, modalTitle.indexOf('('));

		$modal.open(modalDocumentPreview, {
			name: 'modalDocumentPreview',
			passedData: {
				title: modalTitle,
				document: document,
				hideFieldsToMatch: props.hideFieldsToMatch,
			},
			backdrop: true,
		});
	}
</script>
<style>
	@import 'pdfjs-dist/web/pdf_viewer.css';
	.document-preview-container {
		--media-width: 100%;
		--media-height: 100%;

		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 10px;
	}

	.document-preview-container .warning-message {
		width: fit-content;
		margin: auto;
		padding: 5px;
		border-radius: 10px;
		background-color: var(--error-color);
		color: var(--background-color);
	}

	.document-preview-container .warning-message > * {
		display: inline;
	}

	.document-preview-container.is-loading {
		background-color: var(--border-color);
		animation: loadingAnimation 1.3s infinite;
	}

	.document-preview-container figure {
		position: relative;
		margin: 0;
	}

	.document-preview-container figure img,
	.document-preview-container figure video,
	.document-preview-container figure .pdf-container {
		box-sizing: border-box;
		border-radius: 10px;
		border: 1px solid var(--border-color);
	}

	.document-preview-container figure *:not(figcaption) {
		width: var(--media-width);
		height: var(--media-height);
	}

	.document-preview-container figure figcaption {
		opacity: 0;
		position: absolute;
		top: 0;
		left: 0;
		background-color: var(--main-color);
		border-radius: 10px 0 10px 0;
		color: white;
		font-weight: bold;
		transition: all 0.2s ease-out;
	}

	.document-preview-container figure:hover figcaption {
		opacity: 1;
		padding: 5px 10px;
	}

	.document-preview-container .not-supported-container {
		padding: 15px;
		border: 2px solid var(--border-color);
		border-radius: 10px;
		background-color: var(--button-dropdown-list-background-color);
	}

	.document-preview-container .spinner-container > i {
		font-size: 3em;
	}

	@keyframes loadingAnimation {
		0% {
			opacity: 1;
		}

		70% {
			opacity: 0.7;
		}

		100% {
			opacity: 1;
		}
	}
</style>
